import { useMemo, useState } from 'react'
import {
  CircularProgress,
  MenuItem,
  TextField,
  Typography,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { ApexOptions } from 'apexcharts'

import { ApexChart } from 'ApexChart'
import { strings } from 'strings'
import { AutoStatsRover, useAutoStatsRovers } from 'api'

import { formatDurationBreakdown, useAnalyticsRange } from './analyticsShared'
import { RoverReportDialog } from './RoverReportDialog'

const TOP_N = 30
const BAR_COLOR = '#7b1fa2'

type Metric = 'connections' | 'duration'

export function TopRoversChart() {
  const classes = useStyles()
  const { bucketSpec } = useAnalyticsRange()
  const [metric, setMetric] = useState<Metric>('connections')
  const [selected, setSelected] = useState<AutoStatsRover | null>(null)

  const { rovers, loading, error } = useAutoStatsRovers(
    bucketSpec.from,
    bucketSpec.to,
  )

  const visible = useMemo(() => {
    if (!rovers) return [] as AutoStatsRover[]
    const sorted = [...rovers].sort((a, b) =>
      metric === 'connections'
        ? b.totalConnections - a.totalConnections
        : b.totalDurationSeconds - a.totalDurationSeconds,
    )
    return sorted.slice(0, TOP_N)
  }, [rovers, metric])

  const isEmpty = !loading && visible.every((r) => valueFor(r, metric) === 0)

  const options: ApexOptions | null = useMemo(() => {
    if (loading || isEmpty || visible.length === 0) return null
    const data = visible.map((r) => valueFor(r, metric))
    const labels = visible.map(roverLabel)
    return {
      series: [
        {
          name:
            metric === 'connections'
              ? strings.totalConnections
              : strings.totalAirtime,
          data:
            metric === 'duration'
              ? data.map((s) => +(s / 3600).toFixed(2))
              : data,
        },
      ],
      chart: {
        type: 'bar',
        fontFamily: 'vazir',
        height: 420,
        width: '100%',
        animations: { enabled: false },
        toolbar: { show: true },
        events: {
          dataPointSelection: (
            _e,
            _ctx,
            cfg: { dataPointIndex?: number },
          ) => {
            const pi = cfg?.dataPointIndex ?? -1
            const row = visible[pi]
            if (!row) return
            if (valueFor(row, metric) <= 0) return
            setSelected(row)
          },
        },
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '70%',
          dataLabels: { position: 'top' },
        },
      },
      dataLabels: {
        enabled: true,
        formatter: (val) => {
          const v = val as number
          if (v <= 0) return ''
          if (metric === 'duration') return `${v.toFixed(1)}h`
          return String(Math.round(v))
        },
        offsetY: -8,
        style: { fontSize: '11px', colors: ['#333'] },
      },
      xaxis: {
        categories: labels,
        labels: { rotate: -45, style: { fontSize: '11px' } },
      },
      yaxis: {
        title: {
          text:
            metric === 'connections'
              ? strings.totalConnections
              : strings.durationHours,
        },
        labels: {
          formatter: (val) => {
            const n = Number(val)
            return metric === 'duration'
              ? n.toFixed(1)
              : String(Math.round(n))
          },
        },
      },
      legend: { position: 'top', horizontalAlign: 'left' },
      colors: [BAR_COLOR],
      tooltip: {
        y: {
          formatter: (val) => {
            const v = val as number
            if (metric === 'duration') {
              const breakdown = formatDurationBreakdown(v * 3600)
              return `${v.toFixed(2)} ${strings.durationHours} (${breakdown})`
            }
            return String(Math.round(v))
          },
        },
      },
    }
  }, [loading, isEmpty, visible, metric])

  return (
    <div className={classes.card}>
      <div className={classes.cardHeader}>
        <Typography className={classes.title}>
          {strings.topRoversChartTitle}
        </Typography>
        <div className={classes.controls}>
          <TextField
            select
            size="small"
            variant="outlined"
            value={metric}
            onChange={(e) => setMetric(e.target.value as Metric)}
            label={strings.metric}
            className={classes.metricSelect}
          >
            <MenuItem value="connections">
              {strings.topRoversByConnections}
            </MenuItem>
            <MenuItem value="duration">
              {strings.topRoversByDuration}
            </MenuItem>
          </TextField>
        </div>
      </div>
      <div className={classes.chartWrap}>
        {error && (
          <Typography className={classes.status}>
            {strings.error}: {String((error as any)?.message ?? error)}
          </Typography>
        )}
        {!error && loading && (
          <div className={classes.status}>
            <CircularProgress size={24} />
            <Typography>{strings.loading}</Typography>
          </div>
        )}
        {!error && !loading && isEmpty && (
          <Typography className={classes.status}>
            {strings.noTopRoversInRange}
          </Typography>
        )}
        {!error && options && (
          <ApexChart options={options} className={classes.chart} />
        )}
      </div>
      <RoverReportDialog
        open={selected !== null}
        onClose={() => setSelected(null)}
        rover={selected}
        from={bucketSpec.from}
        to={bucketSpec.to}
      />
    </div>
  )
}

function valueFor(r: AutoStatsRover, metric: Metric): number {
  return metric === 'connections' ? r.totalConnections : r.totalDurationSeconds
}

function roverLabel(r: AutoStatsRover): string {
  const name = r.displayName?.trim() || r.username
  // ApexCharts category labels truncate aggressively when the bar count is
  // high. Keep them short and predictable.
  return name.length > 28 ? `${name.slice(0, 27)}…` : name
}

const useStyles = makeStyles((theme) => ({
  card: {
    border: '1px solid #e0e0e0',
    borderRadius: 4,
    overflow: 'hidden',
    width: '100%',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 1),
    background: 'silver',
    flexWrap: 'wrap',
  },
  title: {
    fontWeight: 600,
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
  metricSelect: {
    minWidth: 200,
    background: '#fff',
    '& .MuiOutlinedInput-inputMarginDense': {
      paddingTop: 6,
      paddingBottom: 6,
    },
  },
  chartWrap: {
    width: '100%',
    height: 460,
  },
  chart: {
    width: '100%',
    height: '100%',
  },
  status: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(2),
    height: 360,
    justifyContent: 'center',
  },
}))
